import { Outlet } from 'react-router-dom';
import { useLayout } from './context';
import { Sidebar } from './sidebar';
import { Header } from './header';
import { Toolbar, ToolbarActions, ToolbarHeading } from './toolbar';
import { ToolbarMenu } from './toolbar-menu';
import { ToolbarMenuMobile } from './toolbar-menu-mobile';
import { HeaderBreadcrumbs } from './header-breadcrumbs';
import { BreadcrumbProvider } from './breadcrumb-context';
import { MenuConfig } from '@/config/types';
import { useEmployee } from '@/modules/tenant/hooks/useTenantAuth';
import { CompactWeatherCard } from '@/components/weather/CompactWeatherCard';
import { PortalType } from './sidebar-secondary';
import { QuickAddHost } from '../quick-add/QuickAddHost';

interface WrapperProps {
  menuConfig?: MenuConfig;
  portalType?: PortalType;
  enabledModules?: string[];
  onLogout?: () => void;
  isLogoutPending?: boolean;
}

export function Wrapper({ 
  menuConfig, 
  portalType = 'platform', 
  enabledModules = ['core'],
  onLogout, 
  isLogoutPending 
}: WrapperProps) {
  const {isMobile} = useLayout();
  const employee = useEmployee();
  const displayName =
    employee?.full_name ||
    employee?.name ||
    `${employee?.first_name ?? ''} ${employee?.last_name ?? ''}`.trim() ||
    employee?.email ||
    'User';

  return (
    <BreadcrumbProvider>
      <Header onLogout={onLogout} isLogoutPending={isLogoutPending} portalType={portalType} enabledModules={enabledModules} />
      {!isMobile && (
        <Sidebar 
          menuConfig={menuConfig} 
          portalType={portalType} 
          enabledModules={enabledModules}
          onLogout={onLogout} 
          isLogoutPending={isLogoutPending} 
        />
      )}      

      {/* #1095 — `min-w-0` lets this track the window (see context.tsx), and
          `overflow-x-auto` gives the content area its own horizontal scroll for
          when it hits the floor below, so narrow windows scroll rather than
          clipping the page. */}
      <div className="grow min-w-0 overflow-y-auto overflow-x-auto pt-(--header-height-mobile) lg:pt-[calc(var(--header-height)+var(--toolbar-height))] lg:ps-(--sidebar-width) lg:in-data-[sidebar-open=false]:ps-(--sidebar-collapsed-width) transition-all duration-300">
        <Toolbar>
          <ToolbarHeading>
            {isMobile ? <ToolbarMenuMobile /> : <ToolbarMenu />}
            {!isMobile && (
              <span className="text-sm text-muted-foreground">
                Welcome, <span className="font-medium text-foreground">{displayName}</span>
              </span>
            )}
          </ToolbarHeading>
          <ToolbarActions>
            {!isMobile && <CompactWeatherCard />}
          </ToolbarActions>
        </Toolbar>

        {/* #1095 — the content floor: 1280px (the desktop baseline) minus the
            nav — derived from the nav width so the two cannot drift. Without it
            `min-w-0` above would let the column collapse to ~330px on a laptop,
            which is narrower than the content can use. Below 1280 the content
            area scrolls horizontally instead of squeezing further. */}
        <main className="grow min-w-[calc(80rem-var(--sidebar-width))]" role="content">
          {isMobile && <HeaderBreadcrumbs portalType={portalType} />}
          <Outlet />
        </main>
      </div>
      {portalType === 'tenant' && <QuickAddHost />}
    </BreadcrumbProvider>
  );
}
